/**
 * Parts handed to a mechanic, and his applying them — TRBUD-416.
 *
 * Matt (or the terminal manager) records handing Clark parts. Naming the job
 * makes the hand-over the issue: stock leaves the shelf and the cost lands on
 * the job then. Otherwise the parts sit with Clark until he applies them to a
 * job he may change, and the cost lands then, at the price kept from the
 * hand-over. Stock and cost move once each (Eden, 22 October 2026, decisions
 * 21 to 23).
 *
 * Request bodies and wire shapes only. Browser-safe.
 */
import { z } from "zod";
import { idempotencyKey, positiveDecimalString, strictObject } from "./requests.js";
import { boundedDateString } from "./field-shapes.js";

/**
 * `POST /parts/issue-to-mechanic` — management (`issuePart`).
 *
 * `workOrderId` is optional (decision 22). With it the part is issued to that
 * job at once, as `/parts/issue` does, and nothing enters custody; without it
 * the part moves into the mechanic's hands and its price is kept for his apply.
 */
export const issuePartToMechanicBody = strictObject({
  partId: z.string().uuid(),
  /** The shelf the parts leave. Never a mechanic's custody location. */
  locationId: z.string().uuid(),
  quantity: positiveDecimalString,
  /** Who receives the parts. */
  mechanicUserId: z.string().uuid(),
  workOrderId: z.string().uuid().nullish(),
  note: z.string().trim().max(500).optional(),
  /** Named job only: the MaintainX imported-parts question, as on `/parts/issue`. */
  acknowledgedImportedParts: z.boolean().default(false),
  /**
   * Named job only: the job already carries part lines from a purchase-order
   * bill. Handing over the parts that order bought would cost them twice, so
   * the route asks first (`acknowledge: "purchase_order_bill"`).
   */
  acknowledgedPurchaseOrderBill: z.boolean().default(false),
  idempotencyKey,
});
export type IssuePartToMechanicBody = z.infer<typeof issuePartToMechanicBody>;

/** `POST /work-orders/:id/apply-issued-part` — `applyIssuedParts`. */
export const applyIssuedPartBody = strictObject({
  /** The hand-over the parts came from. */
  issueId: z.string().uuid(),
  quantity: positiveDecimalString,
  acknowledgedImportedParts: z.boolean().default(false),
  /**
   * TRBUD-458: the day the parts were used on the job, which becomes the cost's
   * day. An explicit null says it is not known; omitted keeps the old rule (the
   * day the apply was recorded).
   */
  usedOn: boundedDateString("recorded").nullish(),
  idempotencyKey,
});
export type ApplyIssuedPartBody = z.infer<typeof applyIssuedPartBody>;

/** `POST /parts/custody/return` — management (`issuePart`), decision 23. */
export const returnPartFromCustodyBody = strictObject({
  issueId: z.string().uuid(),
  quantity: positiveDecimalString,
  /** The shelf the parts go back to. Defaults to the one they came from. */
  locationId: z.string().uuid().optional(),
  reason: z.string().trim().min(3).max(500),
  idempotencyKey,
});
export type ReturnPartFromCustodyBody = z.infer<typeof returnPartFromCustodyBody>;

/** What a hand-over did. */
export interface IssueToMechanicReceipt {
  issueId: string;
  /** `job`: issued to the named job at once. `custody`: now in his hands. */
  outcome: "job" | "custody";
  mechanicName: string;
  partNumber: string;
  quantity: string;
  /** The job's cost line, for a named hand-over; null otherwise. */
  costEntryId: string | null;
  amount: string | null;
}

export interface ApplyIssuedPartReceipt {
  applicationId: string;
  inventoryTxnId: string | null;
  costEntryId: string | null;
  amount: string;
  /** What he still holds from that hand-over afterwards. */
  remaining: string;
}

export interface CustodyReturnReceipt {
  returnId: string;
  transferId: string | null;
  remaining: string;
}

/**
 * Parts one person holds from one hand-over that named no job. Quantities are
 * 3-place strings.
 */
export interface IssuedPartBalanceRow {
  issueId: string;
  partId: string;
  partNumber: string;
  description: string;
  unitOfMeasure: string;
  issuedQuantity: string;
  remaining: string;
  issuedAt: string;
  issuedByName: string;
  fromLocationName: string;
  /**
   * The kept price. Null for a caller whose `seeCosts` scope is not `all`: a
   * mechanic sees the price of a part once he has applied it, on the job's own
   * cost line, and not before.
   */
  unitCost: string | null;
}

/** A hand-over that named its job, kept as history: it was issued at once. */
export interface IssuedPartHistoryRow {
  issueId: string;
  partId: string;
  partNumber: string;
  description: string;
  unitOfMeasure: string;
  quantity: string;
  workOrderId: string;
  workOrderNumber: string;
  issuedAt: string;
  issuedByName: string;
}

/** `GET /me/issued-parts`. */
export interface MyIssuedParts {
  /** What he holds now, oldest hand-over first. Empty on a cold start. */
  balance: IssuedPartBalanceRow[];
  /** The newest hand-overs that named a job, at most 20. */
  named: IssuedPartHistoryRow[];
}

/** One person's balance of one part on the Parts summary (management reads it). */
export interface PartCustodyHolding {
  issueId: string;
  mechanicUserId: string;
  mechanicName: string;
  remaining: string;
  issuedAt: string;
  fromLocationId: string;
  fromLocationName: string;
}

/** Somebody parts may be handed to: active, and able to apply them. */
export interface CustodyHolderOption {
  userId: string;
  name: string;
  roles: string[];
}
